<template>
  <div class="ymd-date-input" :class="{ 'is-disabled': disabled }">
    <input
      ref="yearRef"
      class="ymd-part ymd-year"
      type="text"
      inputmode="numeric"
      autocomplete="off"
      placeholder="YYYY"
      maxlength="4"
      :value="localY"
      :disabled="disabled"
      @input="onYear"
      @blur="onBlurPart"
    />
    <span class="ymd-sep">-</span>
    <input
      ref="monthRef"
      class="ymd-part ymd-month"
      type="text"
      inputmode="numeric"
      autocomplete="off"
      placeholder="MM"
      maxlength="2"
      :value="localM"
      :disabled="disabled"
      @input="onMonth"
      @blur="onBlurPart"
    />
    <span class="ymd-sep">-</span>
    <input
      ref="dayRef"
      class="ymd-part ymd-day"
      type="text"
      inputmode="numeric"
      autocomplete="off"
      placeholder="DD"
      maxlength="2"
      :value="localD"
      :disabled="disabled"
      @input="onDay"
      @blur="onBlurPart"
    />
    <button
      v-if="picker"
      type="button"
      class="ymd-picker"
      title="달력에서 선택"
      :disabled="disabled"
      @click="openPicker"
    >
      <input
        ref="pickerRef"
        type="date"
        class="ymd-picker-native"
        tabindex="-1"
        aria-hidden="true"
        :value="modelValue || ''"
        :disabled="disabled"
        :min="min"
        :max="max"
        @change="onPicker"
      />
      <v-icon size="16">mdi-calendar</v-icon>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    modelValue?: string | null
    disabled?: boolean
    picker?: boolean
    min?: string
    max?: string
  }>(),
  {
    modelValue: '',
    disabled: false,
    picker: true,
  }
)

const emit = defineEmits<{
  'update:modelValue': [string]
}>()

const yearRef = ref<HTMLInputElement | null>(null)
const monthRef = ref<HTMLInputElement | null>(null)
const dayRef = ref<HTMLInputElement | null>(null)
const pickerRef = ref<HTMLInputElement | null>(null)

const openPicker = () => {
  if (props.disabled) return
  const el = pickerRef.value
  if (!el) return
  try {
    if (typeof el.showPicker === 'function') {
      el.showPicker()
      return
    }
  } catch {
    // showPicker는 사용자 제스처 밖이면 실패할 수 있음 → 클릭으로 폴백
  }
  el.focus()
  el.click()
}

const localY = ref('')
const localM = ref('')
const localD = ref('')

const parseYmd = (raw?: string | null) => {
  const s = String(raw || '').trim()
  const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
  if (!m) return { y: '', m: '', d: '' }
  return {
    y: m[1],
    m: m[2].padStart(2, '0'),
    d: m[3].padStart(2, '0'),
  }
}

const clampMonth = (raw: string) => {
  if (!raw) return ''
  const n = Math.min(12, Math.max(1, parseInt(raw, 10) || 0))
  return String(n).padStart(2, '0')
}

const clampDay = (raw: string, year: string, month: string) => {
  if (!raw) return ''
  let maxDay = 31
  if (year.length === 4 && month.length === 2) {
    const y = parseInt(year, 10)
    const m = parseInt(month, 10)
    if (y > 0 && m >= 1 && m <= 12) {
      maxDay = new Date(y, m, 0).getDate()
    }
  }
  const n = Math.min(maxDay, Math.max(1, parseInt(raw, 10) || 0))
  return String(n).padStart(2, '0')
}

const applyFromProp = () => {
  const p = parseYmd(props.modelValue)
  localY.value = p.y
  localM.value = p.m
  localD.value = p.d
}
applyFromProp()

watch(
  () => props.modelValue,
  () => {
    const ae = document.activeElement
    if (ae === yearRef.value || ae === monthRef.value || ae === dayRef.value) return
    applyFromProp()
  }
)

/** 월·일 한 자리(예: 1, 5)도 YYYY-MM-DD로 반영 */
const commit = (opts: { padIncomplete?: boolean } = {}) => {
  const pad = !!opts.padIncomplete
  let y = localY.value.replace(/\D/g, '').slice(0, 4)
  let m = localM.value.replace(/\D/g, '').slice(0, 2)
  let d = localD.value.replace(/\D/g, '').slice(0, 2)

  if (pad) {
    if (m.length === 1) m = clampMonth(m)
    if (d.length === 1) d = clampDay(d, y, m.length === 2 ? m : clampMonth(m))
    else if (d.length === 2 && y.length === 4 && m.length === 2) d = clampDay(d, y, m)
    if (m.length === 2) m = clampMonth(m)
  }

  localY.value = y
  localM.value = m
  localD.value = d

  if (!y && !m && !d) {
    if (props.modelValue) emit('update:modelValue', '')
    return
  }

  if (y.length === 4 && m.length === 2 && d.length === 2) {
    const next = `${y}-${m}-${d}`
    if (props.modelValue !== next) emit('update:modelValue', next)
  }
}

const onBlurPart = () => {
  commit({ padIncomplete: true })
}

const onYear = (e: Event) => {
  const el = e.target as HTMLInputElement
  const v = el.value.replace(/\D/g, '').slice(0, 4)
  localY.value = v
  el.value = v
  if (v.length === 4) monthRef.value?.focus()
  commit()
}

const onMonth = (e: Event) => {
  const el = e.target as HTMLInputElement
  let v = el.value.replace(/\D/g, '').slice(0, 2)
  // 2~9 입력 시 즉시 0N 패딩 (1월은 01 또는 10~12 대기)
  if (v.length === 1 && v >= '2' && v <= '9') {
    v = `0${v}`
  }
  localM.value = v
  el.value = v
  if (v.length === 2) {
    localM.value = clampMonth(v)
    el.value = localM.value
    dayRef.value?.focus()
  }
  commit()
}

const onDay = (e: Event) => {
  const el = e.target as HTMLInputElement
  let v = el.value.replace(/\D/g, '').slice(0, 2)
  if (v.length === 1 && v >= '4' && v <= '9') {
    v = `0${v}`
  }
  localD.value = v
  el.value = v
  if (v.length === 2) {
    const m = localM.value.length === 1 ? clampMonth(localM.value) : localM.value
    if (localM.value.length === 1) localM.value = m
    localD.value = clampDay(v, localY.value, m)
    el.value = localD.value
  }
  commit({ padIncomplete: v.length === 2 })
}

const onPicker = (e: Event) => {
  const v = (e.target as HTMLInputElement).value || ''
  if (v) {
    const p = parseYmd(v)
    localY.value = p.y
    localM.value = p.m
    localD.value = p.d
  } else {
    localY.value = ''
    localM.value = ''
    localD.value = ''
  }
  emit('update:modelValue', v)
}
</script>
